import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import ToolTipWrapper from "./ToolTipWrapper";
import MaterialIcons from "../icons/MaterialIcons";
import TextView from "./TextView";
import IconView from "./IconView";
import { BuildContext } from "../classes/BuildContext";

export interface ToolTipInfoMessageProps extends BaseUIProps {
  key?: string;
  label?: string;
  message?: string;
}
/// To store state data for ToolTipInfoMessage
class ToolTipInfoMessageRefs {
  public helpUrlRef: HelpUrlRefState = new HelpUrlRefState();
}

interface HelpUrlRefWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: ToolTipInfoMessageRefs;
}

class HelpUrlRefState extends ObjectObservable {
  private _hover: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _HelpUrlRefWithState extends ObservableComponent<HelpUrlRefWithStateProps> {
  public constructor(props: HelpUrlRefWithStateProps) {
    super(props);

    this.initState();
  }
  public get helpUrlRef(): HelpUrlRefState {
    return this.props.d3eState.helpUrlRef;
  }
  public get d3eState(): ToolTipInfoMessageRefs {
    return this.props.d3eState;
  }
  public initState() {
    super.initState();

    this.updateObservable("helpUrlRef", null, this.helpUrlRef);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["helpUrlRef", "helpUrlRef.hover"], this.rebuild);
  }
  public helpUrlRefOnEnter(event): void {
    return this.helpUrlRef.setHover(true);
  }
  public helpUrlRefOnExit(event): void {
    return this.helpUrlRef.setHover(false);
  }
  public dispose(): void {
    this.helpUrlRef.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    return IconView({
      icon: MaterialIcons.info_outline,
      size: 20,
      onEnter: (event) => {
        this.helpUrlRefOnEnter(event);
      },
      onExit: (event) => {
        this.helpUrlRefOnExit(event);
      },
      className: "x98a",
    });
  }
}
function HelpUrlRefWithState(props: HelpUrlRefWithStateProps) {
  return React.createElement(_HelpUrlRefWithState, props);
}

class _ToolTipInfoMessageState extends ObservableComponent<ToolTipInfoMessageProps> {
  static defaultProps = { label: "", message: "" };
  d3eState: ToolTipInfoMessageRefs = new ToolTipInfoMessageRefs();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ToolTipInfoMessageProps) {
    super(props);

    this.initState();
  }
  public get label(): string {
    return this.props.label;
  }
  public get message(): string {
    return this.props.message;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["label", "message"], this.rebuild);
  }
  public componentDidUpdate(prevProps: ToolTipInfoMessageProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.label !== this.props.label) {
      this.fire("label", this);
    }

    if (prevProps.message !== this.props.message) {
      this.fire("message", this);
    }
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Row({
      mainAxisAlignment: ui.MainAxisAlignment.start,
      children: [
        TextView({
          data: this.label,
          textAlign: cStyle.tTextViewHeadingTextAlignOn,
          style: new ui.TextStyle({
            fontWeight: ui.FontWeight.bold,
            fontFamily: cStyle.tTextViewHeadingFontFamilyOn,
            color: cStyle.tTextViewHeadingColorOn,
            fontSize: cStyle.tTextViewHeadingFontSizeOn,
          }),
          className: "x153",
          key: "0",
        }),
        ui.Center({
          child: ToolTipWrapper({
            message: this.message,
            child: ui.Container({
              alignment: ui.Alignment.topLeft,
              child: HelpUrlRefWithState({ d3eState: this.d3eState }),
              className: "x6dc2",
            }),
          }),
          key: "1",
        }),
      ],
      className: ui.join(this.props.className, "ToolTipInfoMessage"),
      ...copyBaseUIProps(this.props),
    });
  }
  public get helpUrlRef() {
    return this.d3eState.helpUrlRef;
  }
}
export default function ToolTipInfoMessage(props: ToolTipInfoMessageProps) {
  return React.createElement(_ToolTipInfoMessageState, {
    ..._ToolTipInfoMessageState.defaultProps,
    ...props,
  });
}
